import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { logout } from './account';

import type { RootState } from '..';
import { CONSTANTS } from 'common/constants';
import { INDUSTRIES } from 'common/application.const';
import {
    requestCreate,
    requestCreateAmend,
    requestCreateCancel,
    requestCreateRenew,
    type CreateAmendValues,
    type CreateRenewValues,
    requestSave,
    requestSubmit,
    requestSubmitROE,
    requestSubTypes,
    requestAddress,
    requestEdit,
    requestDropdownValues,
    requestSubLanguageGroup,
    requestLanguageGroupList,
    requestSubmitAdditionalInformation,
    requestSubLanguageGroupV2,
    requestLanguageGroupListV2,
    requestSubmitACA,
    requestSubmitKDM,
    requestSubmitAA
} from 'store/api/application.api';
import type { AppState, CreateAppValues, Industry } from 'types/application';

type SaveProps = { payload: any; isSubmit?: boolean; action?: string };

const initialState: AppState = {
    status: CONSTANTS.IDLE,
    dropdownStatus: CONSTANTS.FULFILLED,
    uploadStatus: CONSTANTS.FULFILLED,
    hasTriggerSave: false,

    caseTypeID: '',
    assignmentID: '',
    actionCollect: '',
    application: '',
    applicationType: '',
    applicationForm: '',
    applicationFormDescription: '',

    activeStep: 0,
    activeAccordion: 0,
    isSuccess: false,
    isOnEdit: false,
    isPreviewPage: false,

    dropdownOpts: {},

    validationMsgs: [],
    myInfoAutoFill: false,

    imdaRef: '',
    paymentDetails: {
        netsTxnRef: '',
        netsTxnDtm: '',
        paymentMode: ''
    },
    myinfoCode: '',
    myinfoState: '',
    isAppStarting: false,
    isStatusDeclared: false,
    isSubmissionRequired: false,
    caseStatusLoading: false,
    steps: [],
    selectedTitleSearch: [],
    isDeleteMode: false,
    isCaseRetrieve: false,
    isFormEdit: false,

    selectedItemsPayment: [],

    stepsVisited: [false, false, false, false, false, false],
    nationalityData: [],
    hasGeneralDetailsSaved: false,

    // Amend-specific state
    amendType: null as 'Material' | 'Non-Material' | null,
    isAmendMode: false,

    // Renew-specific state
    renewCaseType: undefined as 'IMDA-IRIS-Work-LICApplication-ACA-RENEW' | 'IMDA-IRIS-Work-LICApplication-ACA-RENEW-NOPAY' | undefined,

    // Cancel-specific state
    cancelType: undefined as 'ACA-CANCEL' | 'AA-CANCEL' | undefined,

    CAProfileUUID: '',
    CARegistrationDetails: null,
    existingTitle: ''
};

export const startNewApp = createAsyncThunk('application/startNewApp', async (values: CreateAppValues) => {
    const { application, applicationType, applicationForm, applicationFormDescription, steps } = values;

    const response = await requestCreate(values);
    if (response?.data?.ID) {
        return {
            isValid: true,
            caseTypeID: response.data.ID as string,
            application,
            applicationType,
            applicationForm,
            applicationFormDescription,
            steps
        };
    }

    return { isValid: false };
});

export const startAmendApp = createAsyncThunk('application/startAmendApp', async (values: CreateAmendValues) => {
    const { application, applicationType, applicationForm, applicationFormDescription, steps } = values;

    const response = await requestCreateAmend(values);
    if (response?.data?.ID) {
        return {
            isValid: true,
            caseTypeID: response.data.ID as string,
            application,
            applicationType,
            applicationForm,
            applicationFormDescription,
            steps,
            amendType: values.amendType
        };
    }

    return { isValid: false };
});

export const startCancelApp = createAsyncThunk(
    'application/startCancelApp',
    async (values: {
        application: string;
        applicationType: string;
        applicationForm: string;
        licenceKey: string;
        applicationFormDescription: string;
        steps: string[];
    }) => {
        const { application, applicationType, applicationForm, applicationFormDescription, steps } = values;

        // Determine cancel type based on applicationType (not application, which is always base)
        const cancelType: 'ACA-CANCEL' | 'AA-CANCEL' | undefined =
            applicationType === 'Accreditation of Certification Authority'
                ? 'ACA-CANCEL'
                : applicationType === 'Registration of Accounting Authority'
                  ? 'AA-CANCEL'
                  : undefined;

        const response = await requestCreateCancel(values);
        if (response?.data?.ID) {
            return {
                isValid: true,
                caseTypeID: response.data.ID as string,
                application: 'IMDA-IRIS-Work-LICApplication', // Use base application like other modes
                applicationType,
                applicationForm,
                applicationFormDescription,
                steps,
                cancelType
            };
        }

        return { isValid: false };
    }
);

export const startRenewApp = createAsyncThunk('application/startRenewApp', async (values: CreateRenewValues) => {
    const { applicationType, applicationForm, applicationFormDescription } = values;

    const response = await requestCreateRenew(values);
    if (response?.data?.ID) {
        // Type assertion: response from requestCreateRenew includes makePayment
        const makePayment = (response as any).makePayment || false;

        // Determine which configuration to use based on payment requirement
        const renewCaseType: 'IMDA-IRIS-Work-LICApplication-ACA-RENEW' | 'IMDA-IRIS-Work-LICApplication-ACA-RENEW-NOPAY' = makePayment
            ? 'IMDA-IRIS-Work-LICApplication-ACA-RENEW'
            : 'IMDA-IRIS-Work-LICApplication-ACA-RENEW-NOPAY';

        // Set steps based on payment requirement
        const renewSteps = makePayment ? ['Application Details', 'Review', 'Make Payment'] : ['Application Details', 'Review'];

        return {
            isValid: true,
            caseTypeID: response.data.ID as string,
            application: 'IMDA-IRIS-Work-LICApplication', // Keep base application type
            applicationType,
            applicationForm,
            applicationFormDescription,
            steps: renewSteps,
            makePayment,
            renewCaseType // Store the full case type separately
        };
    }

    return { isValid: false };
});

export const updateApp = createAsyncThunk('application/updateApp', async ({ payload, isSubmit, action }: SaveProps, { getState }) => {
    const { appForm } = getState() as { appForm: AppState };
    let industry = (INDUSTRIES as Industry[]).find((ind: Industry) => ind.caseType === appForm.application);
    // Safeguard: If the user is actually editing a generic licence case but applicationType identifies ROE, switch to ROE action semantics.
    if (!industry && appForm.applicationType === 'Radio Operator Exam') {
        industry = (INDUSTRIES as Industry[]).find((ind: Industry) => ind.caseType === 'IMDA-IRIS-Work-LICApplication-ROE');
    }
    // If still pointing at a generic licence entry while labelled ROE, ensure actionSave is pyUpdateCaseDetails
    const forceROE = appForm.applicationType === 'Radio Operator Exam';

    // Safeguard: If the user is actually editing a generic licence case but applicationType identifies ACA, switch to ACA action semantics.
    if (!industry && appForm.applicationType === 'Accreditation of Certification Authority') {
        industry = (INDUSTRIES as Industry[]).find((ind: Industry) => ind.caseType === 'IMDA-IRIS-Work-LICApplication-ACA');
    }
    // If still pointing at a generic licence entry while labelled ACA, ensure actionSave is pyUpdateCaseDetails
    const forceACA = appForm.applicationType === 'Accreditation of Certification Authority';

    if (!industry && appForm.applicationType === 'Registration of Accounting Authority') {
        industry = (INDUSTRIES as Industry[]).find((ind: Industry) => ind.caseType === 'IMDA-IRIS-Work-LICApplication-AA');
    }
    // If still pointing at a generic licence entry while labelled ACA, ensure actionSave is pyUpdateCaseDetails
    const forceAA = appForm.applicationType === 'Registration of Accounting Authority';

    const baseActionSave = industry?.actionSave || 'pyUpdateCaseDetails';
    const selectedAction =
        action === 'application-withdraw' || action === 'application-delete'
            ? 'pyChangeStage'
            : forceROE || forceACA || forceAA
              ? 'pyUpdateCaseDetails'
              : baseActionSave;

    if (appForm.caseTypeID === 'F-0001') return { isValid: true, isSubmit };

    if (action === 'videogamesAdditionalInformation') {
        const response = await requestSubmitAdditionalInformation(payload, appForm.caseTypeID);
        return { isValid: Object.keys(response).length > 0, isSubmit };
    }

    if (action === 'submissionOfPendingKDM') {
        const response = await requestSubmitKDM(payload);
        return { isValid: Object.keys(response).length > 0, isSubmit };
    }

    const response = await requestSave(payload, appForm.caseTypeID, selectedAction);
    if (response && isSubmit) {
        // For AEC and Publications
        if (appForm.actionCollect === 'CollectArtDetails' || appForm.actionCollect === 'CollectDetails') {
            const review: { isValid: boolean; validationMsgs?: string[] } = await requestSubmit(
                appForm.caseTypeID,
                industry?.actionCollect || ''
            );
            return { isValid: review.isValid, isSubmit, validationMsgs: review.validationMsgs };
        }

        // For ROE: transition only when current assignment action indicates FEPCaptureCaseDetails (from assignments API).
        if (appForm.applicationType === 'Radio Operator Exam' || industry?.caseType === 'IMDA-IRIS-Work-LICApplication-ROE') {
            const awaitingInitialCapture = appForm.actionCollect === 'FEPCaptureCaseDetails';
            const onFirstStep = appForm.activeStep === 0; // defensive: step still source of truth for initial transition
            if (awaitingInitialCapture && onFirstStep && !appForm.isFormEdit) {
                const review = await requestSubmitROE(appForm.caseTypeID, 'FEPCaptureCaseDetails');
                return { isValid: review.isValid, isSubmit, validationMsgs: (review as any).validationMsgs };
            }
            // Subsequent saves (step > 0) or when assignment changed: do not fire transition again
            return { isValid: true, isSubmit };
        }

        // For ACA: transition only when current assignment action indicates FEPCaptureCaseDetails (from assignments API).
        // Also handle ACA-AMEND which should navigate to review without backend transition
        if (
            appForm.applicationType === 'Accreditation of Certification Authority' ||
            industry?.caseType === 'IMDA-IRIS-Work-LICApplication-ACA' ||
            industry?.caseType === 'IMDA-IRIS-Work-LICApplication-ACA-AMEND' ||
            appForm.application === 'IMDA-IRIS-Work-LICApplication-ACA-AMEND'
        ) {
            // For amend mode, skip backend transition and just navigate to review
            if (
                industry?.caseType === 'IMDA-IRIS-Work-LICApplication-ACA-AMEND' ||
                appForm.application === 'IMDA-IRIS-Work-LICApplication-ACA-AMEND'
            ) {
                return { isValid: true, isSubmit };
            }

            const awaitingInitialCapture = appForm.actionCollect === 'FEPCaptureCaseDetails';
            const onFirstStep = appForm.activeStep === 0; // defensive: step still source of truth for initial transition
            if (awaitingInitialCapture && onFirstStep && !appForm.isFormEdit) {
                const review = await requestSubmitACA(appForm.caseTypeID, 'FEPCaptureCaseDetails');
                return { isValid: review.isValid, isSubmit, validationMsgs: (review as any).validationMsgs };
            }
            // Subsequent saves (step > 0) or when assignment changed: do not fire transition again
            return { isValid: true, isSubmit };
        }

        // For AA: transition only when current assignment action indicates FEPCaptureCaseDetails (from assignments API).
        if (
            appForm.applicationType === 'Registration of Accounting Authority' ||
            industry?.caseType === 'IMDA-IRIS-Work-LICApplication-AA'
        ) {
            const awaitingInitialCapture = appForm.actionCollect === 'FEPCaptureCaseDetails';
            const onFirstStep = appForm.activeStep === 0; // defensive: step still source of truth for initial transition
            if (awaitingInitialCapture && onFirstStep && !appForm.isFormEdit) {
                const review = await requestSubmitAA(appForm.caseTypeID, 'FEPCaptureCaseDetails');
                return { isValid: review.isValid, isSubmit, validationMsgs: (review as any).validationMsgs };
            }
            // Subsequent saves (step > 0) or when assignment changed: do not fire transition again
            return { isValid: true, isSubmit };
        }
    }
    return { isValid: Object.keys(response).length > 0, isSubmit };
});

export const getDropdownOpts = createAsyncThunk('application/getDropdownOpts', async (_, { getState }) => {
    const { appForm } = getState() as { appForm: AppState };
    const { dropdownOpts } = appForm;

    const options = { ...dropdownOpts };

    const dropdownFields = [
        {
            api: 'D_NationalityList',
            property: 'Nationality',
            id: 'Code',
            label: 'Description'
        },
        {
            api: 'D_GetCommonDataByCategory?Category=IDType',
            property: 'IDType',
            id: 'Code',
            label: 'Description'
        },
        {
            api: 'D_GetCommonDataByCategory?Category=PELicenceType',
            property: 'PELicenceType',
            id: 'Code',
            label: 'Description'
        },
        {
            api: 'D_GetCommonDataByCategory?Category=AddressType',
            property: 'AddressType',
            id: 'Code',
            label: 'Description'
        },
        {
            api: 'D_SalutationList',
            property: 'Salutation',
            id: 'Salutation',
            label: 'Salutation'
        },
        {
            api: 'D_CountryCodeList',
            property: 'CountryCode',
            id: 'Code',
            label: 'Description'
        },
        {
            api: 'D_LanguageList',
            property: 'Language',
            id: 'Code',
            label: 'Description'
        },
        {
            api: 'D_GetEventType',
            property: 'IMDA-IRIS-Work-Arts-Type',
            id: 'Type',
            label: 'Type'
        },
        {
            api: 'D_CountryList',
            property: 'Country',
            id: 'Code',
            label: 'Description'
        },
        {
            api: 'D_LanguageList',
            property: 'Language',
            id: 'Code',
            label: 'Description'
        },
        {
            api: 'D_FVGLanguageGroupList',
            property: 'LanguageGroup',
            id: 'Code',
            label: 'Description'
        },
        // Note: SubLanguage is populated via SubLanguageGroup later in the code
        {
            api: 'D_GetCommonData',
            property: 'CommonData',
            id: 'Code',
            label: 'Description',
            category: 'Category'
        },
        {
            api: 'D_GetFVGCommonData?Category=ReleaseYear',
            property: 'ReleaseYear',
            id: 'SubCategory',
            label: 'Description'
        },
        {
            api: 'D_GetFVGCommonData?Category=Region',
            property: 'Region',
            id: 'SubCategory',
            label: 'Description'
        },
        {
            api: 'D_GetFVGCommonData?Category=Platform',
            property: 'Platform',
            id: 'SubCategory',
            label: 'Description'
        },
        {
            api: 'D_GetFVGCommonData?Category=Action',
            property: 'Action',
            id: 'SubCategory',
            label: 'Description'
        },
        {
            api: 'D_GetFVGCommonData?Category=Fighting',
            property: 'Fighting',
            id: 'SubCategory',
            label: 'Description'
        },
        {
            api: 'D_GetFVGCommonData?Category=Racing',
            property: 'Racing',
            id: 'SubCategory',
            label: 'Description'
        },
        {
            api: 'D_GetFVGCommonData?Category=Simulation',
            property: 'Simulation',
            id: 'SubCategory',
            label: 'Description'
        },
        {
            api: 'D_GetFVGCommonData?Category=Other',
            property: 'Other',
            id: 'SubCategory',
            label: 'Description'
        },
        {
            api: 'D_GetFVGCommonData?Category=Adventure',
            property: 'Adventure',
            id: 'SubCategory',
            label: 'Description'
        },
        {
            api: 'D_GetFVGCommonData?Category=Strategy',
            property: 'Strategy',
            id: 'SubCategory',
            label: 'Description'
        },
        {
            api: 'D_GetFVGCommonData?Category=Role Playing Games(RPG)',
            property: 'Role Playing Games(RPG)',
            id: 'SubCategory',
            label: 'Description'
        },
        {
            api: 'D_GetFVGCommonData?Category=ESRB Rating',
            property: 'ESRB Rating',
            id: 'SubCategory',
            label: 'Description'
        },
        {
            api: 'D_GetFVGCommonData?Category=PEGI Rating',
            property: 'PEGI Rating',
            id: 'SubCategory',
            label: 'Description'
        },
        {
            api: 'D_GetFVGCommonData?Category=ACB Rating',
            property: 'ACB Rating',
            id: 'SubCategory',
            label: 'Description'
        },
        {
            api: 'D_GetFVGCommonData?Category=CERO Rating',
            property: 'CERO Rating',
            id: 'SubCategory',
            label: 'Description'
        },
        {
            api: 'D_GetFVGCommonData?Category=FormatsAvailable',
            property: 'FormatsAvailable',
            id: 'SubCategory',
            label: 'Description'
        },
        {
            api: 'D_GetFVGCommonData?Category=DeclarationFormat',
            property: 'DeclarationFormat',
            id: 'SubCategory',
            label: 'Description'
        },
        {
            api: 'D_GetFVGCommonData?Category=SoftwareCategory',
            property: 'SoftwareCategory',
            id: 'SubCategory',
            label: 'Description'
        },
        {
            api: 'D_GetLICCommonDataList?Category=IDType',
            property: 'IDTypeFVG',
            id: 'Code',
            label: 'Description'
        },
        {
            api: 'D_GetLICCommonDataByCategory?Category=Salutation',
            property: 'SalutationFilms',
            id: 'Code',
            label: 'Description'
        },
        {
            api: 'D_ListDecisionChecklist?DecisionType=Rating&CaseType=RCA Submission',
            property: 'RCARating',
            id: 'Code',
            label: 'Description'
        },
        {
            api: 'D_ListDecisionChecklist?DecisionType=Decision&CaseType=RCA Submission',
            property: 'RCADecisionDisplay',
            id: 'Code',
            label: 'Description'
        },
        {
            api: 'D_IndexListOfDecisionChecklist?DecisionType=Decision&CaseType=RCA Submission&SubCategory=FEP',
            property: 'RCADecision',
            id: 'Code',
            label: 'Description'
        },
        {
            api: 'D_GetLICCommonDataList?Category=EntityType',
            property: 'EntityType',
            id: 'Code',
            label: 'Description'
        },
        {
            api: 'D_GetLICCommonDataList?Category=CACompanyDetails',
            property: 'CACompanyDetails',
            id: 'Code',
            label: 'Description'
        },
        {
            api: 'D_GetCommonDataByCategory?Category=SubmissionType',
            property: 'SubmissionType',
            id: 'Code',
            label: 'Description'
        },
        {
            api: 'D_GetLICCommonDataByCatAndSubCat?Category=LicenceCategory&SubCategory=Ship Station Operator’s Certificate of Competency',
            property: 'LicenceCategory',
            id: 'Code',
            label: 'Description'
        },
        {
            api: 'D_GetLICCommonDataList?Category=LICCancelReason',
            property: 'LICCancelReason',
            id: 'Code',
            label: 'Description'
        },
        {
            api: 'D_GetLICCommonDataList?Category=LICCancelReason&SubCategory=AA',
            property: 'LICCancelReasonAA',
            id: 'Code',
            label: 'Description'
        },
        {
            api: 'D_GetFVGCommonData?Category=Qualification',
            property: 'Qualification',
            id: 'Code',
            label: 'Description'
        },
        {
            api: 'D_GetFVGCommonData?Category=EmploymentType',
            property: 'EmploymentType',
            id: 'Code',
            label: 'Description'
        },
        {
            api: 'D_GetLanguageByCategoryCAProfile?Category=SubLanguage&StringVal1=RCAREG',
            property: 'LanguageProficiency',
            id: 'Code',
            label: 'LanguageDescription'
        },
        {
            api: 'D_CountryList',
            property: 'VGCountry',
            id: 'Code',
            label: 'Description'
        },
        {
            api: 'D_LanguageGroupList?LanguageGroup=Language_VG',
            property: 'VGLanguage',
            id: 'Code',
            label: 'Description'
        }
    ];

    await Promise.all(
        dropdownFields.map(async (field: { api: string; property: string; id: string; label: string; category?: string }) => {
            if (!dropdownOpts[field.property] || (dropdownOpts[field.property] && dropdownOpts[field.property].length === 0)) {
                const data = await requestDropdownValues(field.api);
                const mappedData = data.map((opt: any) => {
                    return {
                        optID: opt[field.id],
                        optLabel: opt[field.label],
                        optCategory: opt[field?.category as keyof typeof opt]
                    };
                });
                options[field.property] = mappedData;
            }
        })
    );

    const labelOverrides: Record<string, string> = {
        HS: 'Hong Kong',
        KR: 'South Korea',
        GB: 'UK',
        US: 'USA'
    };

    if (options.VGCountry && options.VGCountry.length > 0) {
        const filteredCountry = [
            'AU',
            'BD',
            'CA',
            'CN',
            'FR',
            'DE',
            'HS',
            'IN',
            'ID',
            'IT',
            'JP',
            'MY',
            'PH',
            'SG',
            'KR',
            'LK',
            'TW',
            'TH',
            'GB',
            'US',
            'XX'
        ];
        options.VGCountry = filteredCountry
            .map((code) => options.VGCountry.find((opt: any) => opt.optID === code))
            .filter(Boolean)
            .map((opt) => ({
                ...opt,
                optLabel: labelOverrides[opt.optID] ?? opt.optLabel
            }));
    }

    if (options.IDTypeFVG && options.IDTypeFVG.length > 0) {
        options.IDTypeFVG = options.IDTypeFVG.filter((opt: any) => opt.optLabel !== 'PASSPORT');
    }

    // Custom mapping for RCARating to show only Description
    if (options.RCARating && options.RCARating.length > 0) {
        const rcaRatingData = await requestDropdownValues('D_ListDecisionChecklist?DecisionType=Rating&CaseType=RCA Submission');
        options.RCARating = rcaRatingData.map((opt: any) => ({
            optID: opt.Code,
            optLabel: opt.Description
        }));
    }

    if (options.RCADecisionDisplay && options.RCADecisionDisplay.length > 0) {
        const rcaDecisionData = await requestDropdownValues('D_ListDecisionChecklist?DecisionType=Decision&CaseType=RCA Submission');
        options.RCADecisionDisplay = rcaDecisionData.map((opt: any) => ({
            optID: opt.Code,
            optLabel: opt.Description // Only show description, not "Code - Description" format
        }));
    }

    const apiSubTypes = await requestSubTypes();
    if (!dropdownOpts.SubType || (dropdownOpts.SubType && dropdownOpts.SubType.length === 0)) {
        const mappedSubTypes = apiSubTypes
            ?.filter((opt: any) => !!opt.SubType && !!opt.Type)
            .map((opt: any) => ({ optID: opt.SubType, optLabel: opt.SubType, group: opt.Type }));
        options.SubType = mappedSubTypes;
    }

    const apiLanguagesGroupList = await requestLanguageGroupListV2();
    const apiSubLanguagesGroup = await requestSubLanguageGroupV2();

    if (apiSubLanguagesGroup.length !== 0) {
        const newSub = apiSubLanguagesGroup?.map((optSub: any) => {
            const r = {
                Code: optSub.Code,
                Description: optSub.Description,
                LanguageGroup: optSub.SubCategory,
                LanguageGroupSortOrder: optSub.SortOrder,
                pxObjClass: optSub.pxObjClass
            };
            return r;
        });

        const mappedSubLanguageGroup = newSub?.map((opt: any) => ({
            optID: opt.Code,
            optLabel: opt.Description,
            group: opt.LanguageGroup
        }));

        options.SubLanguageGroup = mappedSubLanguageGroup;
    }

    if (apiLanguagesGroupList.length !== 0) {
        const mappedLanguageGroupList = apiLanguagesGroupList?.map((opt: any) => ({
            optID: opt.Code,
            optLabel: opt.Description
        }));
        options.LanguageGroup = mappedLanguageGroupList;
    }

    if (!dropdownOpts['LanguageGroup'] || (dropdownOpts['LanguageGroup'] && dropdownOpts['LanguageGroup'].length === 0)) {
        /* "Attachment" = Required Materials "ClassifierAction" = Route to Approving Officer for Approval "Format" = Online Link "POAction"= Route to Processing Officer Group Pending Media "POActionDCP" = Route to Approving Officer Group Pending Allocation "Purpose" = Theatrical Release "Room" = Room A "Subtitle" = European Languages "Type"= FEATURE */
        const FilmsCommonDataList = await requestDropdownValues('D_FilmsCommonDataList');
        const filmsDropdownOptions = [
            {
                option: 'IMDA-IRIS-Work-Films-LanguageGroup',
                category: 'Subtitle'
            },
            {
                option: 'IMDA-IRIS-Work-Films-Subtitle',
                category: 'Films'
            },
            {
                option: 'IMDA-IRIS-Work-Films-Type',
                category: 'FormatType'
            },
            {
                option: 'IMDA-IRIS-Work-Films-Format',
                category: 'Format'
            },
            {
                option: 'IMDA-IRIS-Work-Films-Format-NonDCP',
                category: 'Format'
            },
            {
                option: 'IMDA-IRIS-Work-Films-Attachment',
                category: 'Attachment'
            },
            {
                option: 'IMDA-IRIS-Work-Films-Purpose',
                category: 'Purpose'
            },
            {
                option: 'IMDA-IRIS-Work-Films-Room',
                category: 'Room'
            },
            {
                option: 'IMDA-IRIS-Work-Films-ClassifierAction',
                category: 'ClassifierAction'
            },
            {
                option: 'IMDA-IRIS-Work-Films-POAction',
                category: 'POAction'
            },
            {
                option: 'IMDA-IRIS-Work-Films-POActionDCP',
                category: 'POActionDCP'
            },
            {
                option: 'IMDA-IRIS-Work-Films-Rating',
                category: 'RatingDecision'
            },
            {
                option: 'IMDA-IRIS-Work-Films-Decision',
                category: 'RatingDecision'
            }
        ];

        filmsDropdownOptions.forEach((item) => {
            if (item.option === 'IMDA-IRIS-Work-Films-LanguageGroup') {
                options[item.option] = FilmsCommonDataList.filter(
                    (data: any) => data.Category === item.category && data.Description !== 'NIL'
                ).map((sub: any) => {
                    return {
                        optID: sub.Code,
                        optLabel: sub.Description,
                        group: sub.LanguageGroup
                    };
                });
            } else if (item.option === 'IMDA-IRIS-Work-Films-Format') {
                const customOrder = ['DCP', 'DCP3D', 'EDCP', 'ONLNK', 'THD', 'HD', 'BRE', 'BR3D', 'BRUHD', 'DVD', 'DVDR', 'TPOFD', 'OTH'];

                options[item.option] = customOrder.map((id) => {
                    const sub = FilmsCommonDataList.find((data: any) => data.Category === item.category && data.Code === id);

                    return {
                        optID: sub.Code,
                        optLabel: sub.Description
                    };
                });
            } else if (item.option === 'IMDA-IRIS-Work-Films-Format-NonDCP') {
                const customOrder = ['ONLNK', 'THD', 'HD', 'BRE', 'BR3D', 'BRUHD', 'DVD', 'DVDR', 'TPOFD', 'OTH'];

                options[item.option] = customOrder.map((id) => {
                    const sub = FilmsCommonDataList.find(
                        (data: any) =>
                            data.Category === item.category &&
                            data.Code !== 'DCP' &&
                            data.Code !== 'EDCP' &&
                            data.Code !== 'DCP3D' &&
                            data.Code === id
                    );

                    return {
                        optID: sub.Code,
                        optLabel: sub.Description
                    };
                });
            } else if (item.option === 'IMDA-IRIS-Work-Films-Subtitle') {
                const customOrder = ['EN', 'CHI', 'TA', 'MY', 'OTH'];

                options[item.option] = customOrder.map((id) => {
                    const sub = FilmsCommonDataList.find((data: any) => data.Category === item.category && data.Code === id);

                    return {
                        optID: sub.Code,
                        optLabel: sub.Description
                    };
                });
            } else {
                options[item.option] = FilmsCommonDataList.filter((data: any) => data.Category === item.category).map((sub: any) => {
                    if (sub.Description === 'NIL') {
                        return {
                            optID: sub.Description,
                            optLabel: sub.Description
                        };
                    } else {
                        return {
                            optID: sub.Code,
                            optLabel: sub.Description
                        };
                    }
                });
            }
        });
    }

    // Films Classification decision codes — authoritative source for code/description mapping
    if (options['IMDA-IRIS-Work-Films-Decision'] && options['IMDA-IRIS-Work-Films-Decision'].length > 0) {
        const filmsDecisionData = await requestDropdownValues(
            'D_ListDecisionChecklist?DecisionType=Decision&CaseType=Films Classification'
        );
        if (Array.isArray(filmsDecisionData) && filmsDecisionData.length > 0) {
            options['IMDA-IRIS-Work-Films-Decision'] = filmsDecisionData.map((opt: any) => ({
                optID: opt.Code,
                optLabel: opt.Description
            }));
        }
    }

    // Films Classification rating codes — authoritative source for code/description mapping
    if (options['IMDA-IRIS-Work-Films-Rating'] && options['IMDA-IRIS-Work-Films-Rating'].length > 0) {
        const filmsRatingData = await requestDropdownValues(
            'D_ListDecisionChecklist?DecisionType=Rating&CaseType=Films Classification'
        );
        if (Array.isArray(filmsRatingData) && filmsRatingData.length > 0) {
            options['IMDA-IRIS-Work-Films-Rating'] = filmsRatingData.map((opt: any) => ({
                optID: opt.Code,
                optLabel: opt.Description
            }));
        }
    }

    if (
        !dropdownOpts['IMDA-IRIS-Work-Publication-Type'] ||
        (dropdownOpts['IMDA-IRIS-Work-Publication-Type'] && dropdownOpts['IMDA-IRIS-Work-Publication-Type'].length === 0)
    ) {
        const PublicationList = await requestDropdownValues('D_PublicationList');
        const mappedPublicationList = PublicationList.filter(
            (data: any) => !data.ItemType || data.pzInsKey === 'IMDA-IRIS-DATA-PUBLICATION OTHER TYPES OF PUBLICATIONS!1'
        ).map((data: any) => {
            return {
                optID: data.Type,
                optLabel: data.Type
            };
        });
        options['IMDA-IRIS-Work-Publication-Type'] = mappedPublicationList;
    }

    return {
        isValid: true,
        options
    };
});

export const setEdit = createAsyncThunk('application/edit', async (_, { getState }) => {
    const { appForm } = getState() as { appForm: AppState };
    const response = await requestEdit(appForm.caseTypeID);
    return { isValid: Object.keys(response).length > 0 };
});

export const applicationFormSlice = createSlice({
    name: 'appForm',
    initialState,
    reducers: {
        updateStep(state, action) {
            window.scrollTo({
                top: 200,
                left: 0,
                behavior: 'smooth'
            });
            state.activeStep = action.payload;
            state.isOnEdit = false;
        },
        updateAccordion(state, action) {
            state.activeAccordion = action.payload;
            const top = 48 * Number(action.payload);
            window.scrollTo({
                top: 290 + top,
                left: 0,
                behavior: 'smooth'
            });
        },
        updateStatus(state, action) {
            state.status = action.payload;
        },
        updatePreviewState(state, action) {
            state.isPreviewPage = action.payload;
        },
        updateUploadStatus(state, action) {
            state.uploadStatus = action.payload;
        },
        setSuccess(state) {
            state.isSuccess = true;
        },
        clearData(state) {
            // to remove reducer
            state.status = CONSTANTS.IDLE;
            state.dropdownStatus = CONSTANTS.FULFILLED;
            state.uploadStatus = CONSTANTS.FULFILLED;
            state.validationMsgs = [];
            state.isSuccess = false;
            state.imdaRef = '';
            state.paymentDetails = {
                netsTxnRef: '',
                netsTxnDtm: '',
                paymentMode: ''
            };
            state.hasTriggerSave = false;
            state.selectedItemsPayment = [];
            // state.myInfoAutoFill = false;
        },
        clearApplication(state) {
            state.applicationForm = '';
            state.applicationType = '';
            state.isStatusDeclared = false;
            state.isSubmissionRequired = false;
            state.isFormEdit = false;
            state.selectedTitleSearch = '';
            state.stepsVisited = [false, false, false, false, false, false];
            state.hasGeneralDetailsSaved = false;
            state.isCaseRetrieve = false;
            state.steps = [];
            state.amendType = null;
            state.isAmendMode = false;
            state.renewCaseType = undefined;
            state.cancelType = undefined;
            state.CARegistrationDetails = null;
            state.CAProfileUUID = '';
        },
        setEditApp(state, action) {
            const { application, applicationType, applicationForm, applicationFormDescription, steps, isAmendMode, amendType } =
                action.payload;
            state.application = application;
            state.applicationType = applicationType;
            state.applicationForm = applicationForm;
            state.applicationFormDescription = applicationFormDescription;
            state.steps = steps;
            state.isCaseRetrieve = false;

            // Update amend state if provided
            if (isAmendMode !== undefined) {
                state.isAmendMode = isAmendMode;
            }
            if (amendType !== undefined) {
                state.amendType = amendType;
            }
        },
        updateOnEdit(state, action) {
            const { caseTypeID, application, applicationType, applicationForm, step, isPreviewPage } = action.payload;
            state.status = CONSTANTS.IDLE;
            state.dropdownStatus = CONSTANTS.FULFILLED;
            state.uploadStatus = CONSTANTS.FULFILLED;
            state.validationMsgs = [];

            state.caseTypeID = caseTypeID;
            state.application = application;
            state.applicationType = applicationType;
            state.applicationForm = applicationForm;

            // Detect if this is an amend mode based on application caseType
            const isAmendApplication = application?.includes('-AMEND');
            state.isAmendMode = isAmendApplication;

            state.activeStep = step;
            // Set activeAccordion to 0 for amend mode (no summary form)
            state.activeAccordion = state.isAmendMode ? 0 : 0;
            state.isSuccess = false;
            state.isOnEdit = true;
            state.isPreviewPage = isPreviewPage || false;

            // state.dropdownOpts = {};
            state.myInfoAutoFill = true;
        },
        resetTriggerSave(state) {
            state.hasTriggerSave = false;
        },
        setAssignment(state, action) {
            const { actionCollect, assignmentID } = action.payload;
            state.actionCollect = actionCollect;
            state.assignmentID = assignmentID;
        },
        setAutoFill(state) {
            state.myInfoAutoFill = true;
        },
        clearAutoFill(state) {
            state.myInfoAutoFill = false;
        },
        setImdaRef(state, action) {
            state.imdaRef = action.payload || '';
        },
        setPayment(state, action) {
            const { netsTxnRef = '', netsTxnDtm = '', paymentMode = '' } = action.payload;
            state.paymentDetails = {
                netsTxnRef,
                netsTxnDtm,
                paymentMode
            };
        },
        setMyInfo(state, action) {
            state.myinfoCode = action.payload;
        },
        setMyInfoState(state, action) {
            state.myinfoState = action.payload;
        },
        clearMyInfo(state) {
            state.myinfoCode = '';
        },
        setIsStarting(state) {
            state.isAppStarting = true;
        },
        removeIsStarting(state) {
            state.isAppStarting = false;
        },
        setIsCaseStatusLoading(state) {
            state.caseStatusLoading = true;
        },
        setStatusDeclaration(state, action) {
            state.isStatusDeclared = action.payload;
        },
        setSubmissionRequired(state, action) {
            state.isSubmissionRequired = action.payload;
        },
        removeIsCaseStatusLoading(state) {
            state.caseStatusLoading = false;
        },
        setFormatOptions(state, action) {
            state.dropdownOpts = action.payload;
        },
        setTitleSearchSelected(state, action) {
            state.selectedTitleSearch = [...state.selectedTitleSearch, ...action.payload];
            state.isDeleteMode = false;
        },
        removeTitleSearchSelected: (state, action) => {
            const index = action.payload;
            state.selectedTitleSearch = state.selectedTitleSearch.filter((_: any, i: any) => i !== index);
            state.isDeleteMode = true;
        },
        setSelectedItemsPayment(state, action) {
            state.selectedItemsPayment = action.payload;
        },
        setIsCaseRetrieve(state, action) {
            state.isCaseRetrieve = action.payload;
        },
        setIsFormEdit(state, action) {
            state.isFormEdit = action.payload;
        },
        setStepsVisited: (state, action) => {
            action.payload.forEach((index: number) => {
                state.stepsVisited[index] = true;
            });
        },
        setIsGeneralDetailsSaved: (state, action) => {
            state.hasGeneralDetailsSaved = action.payload;
        },
        setSteps: (state, action) => {
            state.steps = action.payload;
        },
        setAmendType: (state, action) => {
            state.amendType = action.payload;
            state.isAmendMode = !!action.payload;
        },
        clearAmendMode: (state) => {
            state.amendType = null;
            state.isAmendMode = false;
        },
        setCAProfileUUID: (state, action) => {
            state.CAProfileUUID = action.payload;
        },
        setCARegistrationDetails: (state, action) => {
            state.CARegistrationDetails = action.payload;
        },
        setExistingTitle: (state, action) => {
            state.existingTitle = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(startNewApp.pending, (state) => {
                state.status = CONSTANTS.LOADING;
                state.isSuccess = false;
                state.caseTypeID = '';
                state.application = '';
                state.applicationType = '';
                state.activeStep = 0;
                state.activeAccordion = 0;
                state.isOnEdit = false;
                state.myInfoAutoFill = false;
                state.isStatusDeclared = false;
                state.isSubmissionRequired = false;
                state.isFormEdit = false;
                state.selectedTitleSearch = '';
            })
            .addCase(startNewApp.fulfilled, (state, action) => {
                const { isValid, caseTypeID, application, applicationType, applicationForm, applicationFormDescription } = action.payload;
                if (isValid) {
                    state.status = CONSTANTS.FULFILLED;
                    if (caseTypeID) state.caseTypeID = caseTypeID;
                    if (application) state.application = application;
                    if (applicationType) state.applicationType = applicationType;
                    if (applicationForm) state.applicationForm = applicationForm;
                    if (applicationFormDescription) state.applicationFormDescription = applicationFormDescription;
                    state.renewCaseType = undefined; // Clear renew case type for new applications
                    state.cancelType = undefined; // Clear cancel type for new applications
                } else {
                    state.status = CONSTANTS.FAILED;
                }
            })
            .addCase(startAmendApp.pending, (state) => {
                state.status = CONSTANTS.LOADING;
                state.isSuccess = false;
                state.caseTypeID = '';
                state.application = '';
                state.applicationType = '';
                state.activeStep = 0;
                state.activeAccordion = 0;
                state.isOnEdit = false;
                state.myInfoAutoFill = false;
                state.isStatusDeclared = false;
                state.isSubmissionRequired = false;
                state.isFormEdit = false;
                state.selectedTitleSearch = '';
            })
            .addCase(startAmendApp.fulfilled, (state, action) => {
                const { isValid, caseTypeID, application, applicationType, applicationForm, applicationFormDescription, amendType } =
                    action.payload;
                if (isValid) {
                    state.status = CONSTANTS.FULFILLED;
                    if (caseTypeID) state.caseTypeID = caseTypeID;
                    if (application) state.application = application;
                    if (applicationType) state.applicationType = applicationType;
                    if (applicationForm) state.applicationForm = applicationForm;
                    if (applicationFormDescription) state.applicationFormDescription = applicationFormDescription;
                    if (amendType) {
                        state.amendType = amendType;
                        state.isAmendMode = true;
                        // Accordion starts at 0 (first form) since summary is not included in amend mode
                        state.activeAccordion = 0;
                    }
                    state.renewCaseType = undefined; // Clear renew case type for amend applications
                    state.cancelType = undefined; // Clear cancel type for amend applications
                } else {
                    state.status = CONSTANTS.FAILED;
                }
            })
            .addCase(startCancelApp.pending, (state) => {
                state.status = CONSTANTS.LOADING;
                state.isSuccess = false;
                state.caseTypeID = '';
                state.application = '';
                state.applicationType = '';
                state.activeStep = 0;
                state.activeAccordion = 0;
                state.isOnEdit = false;
                state.myInfoAutoFill = false;
                state.isStatusDeclared = false;
                state.isSubmissionRequired = false;
                state.isFormEdit = false;
                state.selectedTitleSearch = '';
            })
            .addCase(startCancelApp.fulfilled, (state, action) => {
                const { isValid, caseTypeID, application, applicationType, applicationForm, applicationFormDescription, cancelType } =
                    action.payload;
                if (isValid) {
                    state.status = CONSTANTS.FULFILLED;
                    if (caseTypeID) state.caseTypeID = caseTypeID;
                    if (application) state.application = application;
                    if (applicationType) state.applicationType = applicationType;
                    if (applicationForm) state.applicationForm = applicationForm;
                    if (applicationFormDescription) state.applicationFormDescription = applicationFormDescription;
                    state.cancelType = cancelType; // Store cancel type (typed as 'ACA-CANCEL' | 'AA-CANCEL' | undefined)
                    state.activeAccordion = 0;
                    state.renewCaseType = undefined; // Clear renew case type for cancel applications
                } else {
                    state.status = CONSTANTS.FAILED;
                }
            })
            .addCase(startRenewApp.pending, (state) => {
                state.status = CONSTANTS.LOADING;
                state.isAppStarting = true;
            })
            .addCase(startRenewApp.fulfilled, (state, action) => {
                const {
                    isValid,
                    caseTypeID,
                    application,
                    applicationType,
                    applicationForm,
                    applicationFormDescription,
                    steps,
                    renewCaseType
                } = action.payload;
                if (isValid) {
                    state.status = CONSTANTS.FULFILLED;
                    if (caseTypeID) state.caseTypeID = caseTypeID;
                    if (application) state.application = application;
                    if (applicationType) state.applicationType = applicationType;
                    if (applicationForm) state.applicationForm = applicationForm;
                    if (applicationFormDescription) state.applicationFormDescription = applicationFormDescription;
                    if (steps) state.steps = steps;
                    state.renewCaseType = renewCaseType; // Store the full renew case type (typed as union)
                    state.activeStep = 0; // Initialize activeStep to 0
                    state.activeAccordion = 0;
                    state.isAppStarting = false;
                    state.cancelType = undefined; // Clear cancel type for renew applications
                } else {
                    state.status = CONSTANTS.FAILED;
                    state.isAppStarting = false;
                }
            })
            .addCase(updateApp.pending, (state) => {
                state.status = CONSTANTS.LOADING;
                window.scrollTo({
                    top: 300,
                    left: 0,
                    behavior: 'smooth'
                });
            })
            .addCase(updateApp.fulfilled, (state, action) => {
                const { isValid, isSubmit, validationMsgs } = action.payload;
                const { activeStep } = state;
                if (isValid) {
                    state.hasTriggerSave = true;
                    state.status = CONSTANTS.FULFILLED;
                    state.validationMsgs = validationMsgs || [];
                    if (isSubmit && !validationMsgs) {
                        state.activeStep = activeStep + 1;
                        state.isOnEdit = false;
                    }
                } else {
                    state.status = CONSTANTS.FAILED;
                }
            })
            .addCase(getDropdownOpts.pending, (state) => {
                state.dropdownStatus = CONSTANTS.LOADING;
            })
            .addCase(getDropdownOpts.fulfilled, (state, action) => {
                const { isValid, options } = action.payload;
                if (isValid) {
                    state.dropdownStatus = CONSTANTS.FULFILLED;
                    if (options) state.dropdownOpts = options;
                } else {
                    state.dropdownStatus = CONSTANTS.FAILED;
                }
            })
            .addCase(setEdit.pending, (state) => {
                state.status = CONSTANTS.LOADING;
            })
            .addCase(setEdit.fulfilled, (state, action) => {
                const { isValid } = action.payload;
                if (isValid) {
                    state.status = CONSTANTS.FULFILLED;
                } else {
                    state.status = CONSTANTS.FAILED;
                }
            })
            // Clear payment-related state on logout so persisted activeStep/caseTypeID
            // don't land the user back on the payment page after logging out and back in.
            .addCase(logout, (state) => {
                state.activeStep = 0;
                state.isSuccess = false;
                state.caseTypeID = '';
                state.imdaRef = '';
                state.paymentDetails = { netsTxnRef: '', netsTxnDtm: '', paymentMode: '' };
            });
    }
});

export const {
    updateStep,
    updateAccordion,
    updateStatus,
    updatePreviewState,
    updateUploadStatus,
    setSuccess,
    clearData,
    setEditApp,
    updateOnEdit,
    resetTriggerSave,
    setAssignment,
    setAutoFill,
    clearAutoFill,
    setImdaRef,
    setPayment,
    setMyInfo,
    setMyInfoState,
    clearMyInfo,
    setIsStarting,
    removeIsStarting,
    setIsCaseStatusLoading,
    setStatusDeclaration,
    setSubmissionRequired,
    removeIsCaseStatusLoading,
    clearApplication,
    setFormatOptions,
    setTitleSearchSelected,
    removeTitleSearchSelected,
    setSelectedItemsPayment,
    setIsCaseRetrieve,
    setIsFormEdit,
    setStepsVisited,
    setIsGeneralDetailsSaved,
    setSteps,
    setAmendType,
    clearAmendMode,
    setCAProfileUUID,
    setCARegistrationDetails,
    setExistingTitle
} = applicationFormSlice.actions;

export const appForm = (state: RootState) => state.appForm;

export default applicationFormSlice.reducer;
